import { isSameDay, subDays } from 'date-fns';

import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { FEATURES_FLAG_KEYS } from '@/features/featuresFlag/apis/featureFlag.keys';
import { FolderItem } from '@/features/folders/types';
import { COUNTRIES_NAME, CROSSING_DIRECTION_CITY, CROSSING_DIRECTION_COUNTRY } from '@/features/shared/constants';
import { isBorderCrossingRoRo, isBorderCrossingRoRoEmptyV2 } from '@/features/shared/helpers/typologies';

export function useFolderLine({
  folder: { direction, crossingDate, folderId, folderTypologyId },
  featuresFlag,
}: {
  folder: FolderItem;
  featuresFlag: Map<string, boolean>;
}) {
  const navigate = useNavigate();

  const IsBPE = featuresFlag.get(FEATURES_FLAG_KEYS.BPE);
  const isCustomsProENS =
    featuresFlag.get(FEATURES_FLAG_KEYS.SHERPASS) &&
    (isBorderCrossingRoRo({ folderTypologyId }) || isBorderCrossingRoRoEmptyV2({ folderTypologyId }));

  const handleNavigateToFolderDetail = useCallback(() => {
    if (isCustomsProENS) {
      navigate(`/typologies/${folderTypologyId}/folders/${folderId}/applicant-transmitted-documents`);
    } else {
      navigate(`/folders/${folderId}/customs-documents`);
    }
  }, [folderId, folderTypologyId, isCustomsProENS, navigate]);

  const { departure, arrival } =
    direction === CROSSING_DIRECTION_COUNTRY.EUUK
      ? {
          departure: IsBPE ? CROSSING_DIRECTION_CITY.Ca : COUNTRIES_NAME.EU,
          arrival: IsBPE ? CROSSING_DIRECTION_CITY.Fo : COUNTRIES_NAME.GB,
        }
      : {
          arrival: IsBPE ? CROSSING_DIRECTION_CITY.Ca : COUNTRIES_NAME.EU,
          departure: IsBPE ? CROSSING_DIRECTION_CITY.Fo : COUNTRIES_NAME.GB,
        };

  const dayBeforeCrossingDate = subDays(new Date(crossingDate), 1);
  const isTodayIsTheDayBeforeCrossingDate = isSameDay(dayBeforeCrossingDate, new Date());

  return {
    isTodayIsTheDayBeforeCrossingDate,
    departure,
    arrival,
    handleNavigateToFolderDetail,
  };
}
